import {Gallery_GalleryVideo} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/gallery.types';
import {useTranslation} from '@aboutyou/i18n';
import {useTrackFeature} from '@aboutyou/tracking';
import {Fade, Grow} from '@aboutyou/transitions';
import {
  COLORS,
  GenericIconName,
  Icon,
  UnstyledButton,
  Video,
  useCallbackDelayed,
  useInViewEffect,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {
  FC,
  MouseEvent,
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

interface VideoSlideProps extends Gallery_GalleryVideo {
  isFullModeEnabled: boolean;
  shouldPreload: boolean;
}

export const VideoSlide: FC<VideoSlideProps> = ({
  video,
  isFullModeEnabled,
  shouldPreload,
}) => {
  const {t} = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useVideoSlide({isFullModeEnabled, videoRef});
  const [isError, setIsError] = useState(false);

  const reloadVideo = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    startTransition(() => {
      setIsError(false);
    });
  };

  const onError = () => {
    startTransition(() => {
      setIsError(true);
    });
  };

  const aspectRatio = video.aspectRatio
    ? `${video.aspectRatio.width} / ${video.aspectRatio.height}`
    : '3 / 4';

  return (
    <Wrapper
      ref={containerRef}
      key={`videoSlide${isError ? '-error' : ''}`}
      data-testid="videoSlide"
    >
      <StyledVideo
        disablePictureInPicture
        disableRemotePlayback
        playsInline
        muted
        loop
        ref={videoRef}
        sources={video.sources}
        preload={shouldPreload ? 'auto' : 'none'}
        poster={video.fallbackImage.url}
        controls={false}
        aspectRatio={aspectRatio}
        onError={onError}
      />

      <Fade mounted={isError} duration={350}>
        <div>
          <Grow mounted={isError} duration={350} direction="center">
            <ButtonWrapper>
              <ReloadButton
                data-testid="reloadVideoButton"
                onClick={reloadVideo}
                aria-label={t('Reset')}
              >
                <Icon
                  asset={GenericIconName.Reset}
                  size="M"
                  fill={COLORS.White}
                />
              </ReloadButton>
            </ButtonWrapper>
          </Grow>
        </div>
      </Fade>
    </Wrapper>
  );
};

const useVideoSlide = ({
  isFullModeEnabled,
  videoRef,
}: {
  isFullModeEnabled: boolean;
  videoRef: React.RefObject<HTMLVideoElement | null>;
}) => {
  const trackFeature = useTrackFeature();
  const trackedStages = useRef<string[]>([]);

  const trackSlide = useCallback(
    function trackSlideCb(params: {
      trackKey: string;
      mode: 'adp' | 'gallery';
      eventType: 'impression' | 'submit';
    }) {
      if (!trackedStages.current.includes(params.trackKey)) {
        trackedStages.current.push(params.trackKey);
        trackFeature({
          eventType: params.eventType,
          label: `video_clip|${params.mode}`,
          name: 'video_clip',
        });
      }
    },
    [trackFeature],
  );

  const trackSlideImpression = () => {
    const mode = isFullModeEnabled ? 'gallery' : 'adp';
    trackSlide({
      trackKey: `impression-${mode}`,
      mode,
      eventType: 'impression',
    });
  };

  const {delayedCallback: trackSlideSubmit, cancelCallback: cancelSlideSubmit} =
    useCallbackDelayed(function delayedTrackSlideSubmit() {
      const mode = isFullModeEnabled ? 'gallery' : 'adp';
      trackSlide({
        trackKey: `submit-${mode}`,
        mode,
        eventType: 'submit',
      });
    }, 5000);

  const {ref: containerRef} = useInViewEffect<HTMLDivElement>(
    inView => {
      if (inView) {
        trackSlideImpression();
        trackSlideSubmit();
        videoRef.current?.play()?.catch(() => {
          // No need to handle the error here, it will be handled by the video element itself
        });
      } else {
        cancelSlideSubmit();
        videoRef.current?.pause();
      }
    },
    {
      threshold: 0.05,
    },
  );

  useEffect(() => {
    return cancelSlideSubmit;
  }, [isFullModeEnabled]);

  return containerRef;
};

const Wrapper = styled.div`
  position: relative;
`;

const StyledVideo = styled(Video)<{aspectRatio?: string}>`
  aspect-ratio: ${props => (props.aspectRatio ? props.aspectRatio : '')};
  height: 100%;
  width: 100%;
`;

const ReloadButton = styled(UnstyledButton)`
  --button-size: 64px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  flex-basis: var(--button-size);
  height: var(--button-size);
  width: var(--button-size);
`;

const ButtonWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
